import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";
import { getD3AuthDomain } from "../../providers/D3AuthProvider";
import { getIsWebView } from "../../utils/webView";
import { Loading } from "../App";

// SPL-specific silent logout that suppresses 403 errors
const silentLogout = async () => {
  try {
    const response = await fetch("/api/player/logout/", {
      method: "GET",
      credentials: "include",
    });
    // Ignore 403 - expected if session expired
    if (!response.ok && response.status !== 403) {
      console.warn("Logout failed:", response.status);
    }
  } catch (err) {
    // Network errors are safe to ignore during logout
    console.warn("Logout error:", err);
  }
};

const LogoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[3]};
  align-items: center;
  justify-content: center;
  margin: ${({ theme }) => theme.space[4]};
`;

const Logout = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Clear Firebase/OIDC session flags
        sessionStorage.removeItem("firebase_authenticated");
        localStorage.removeItem("firebase_loginError");

        // For webview, just redirect to homepage
        if (getIsWebView()) {
          await silentLogout();
          window.location.replace("/?webview=1");
          return;
        }

        // For web: Follow D3 logout flow (Peter's specification)
        // 1. Call backend logout to clear Django session (suppress errors)
        await silentLogout();

        // 2. Remove user from OIDC client storage (clears local session)
        await auth.removeUser();

        // 3. Redirect to D3 static logout page which handles identity provider sign-out
        const postLogoutRedirectUri = window.location.origin + "/";

        // Get D3 auth domain from configured authority and construct logout URL
        // e.g., https://account.spl-dev.deltatre.digital/identity/v1/main
        // -> https://account.spl-dev.deltatre.digital/adb2c-resources/main/logout.html
        const authDomain = getD3AuthDomain();
        const baseDomain = authDomain.replace(/\/identity\/v1\/main$/, "");

        const d3LogoutUrl = `${baseDomain}/adb2c-resources/main/logout.html?post_logout_redirect_uri=${encodeURIComponent(
          postLogoutRedirectUri,
        )}`;

        window.location.href = d3LogoutUrl;
      } catch (err) {
        setError(true);
        // Fallback: try to remove user and redirect manually
        setTimeout(async () => {
          try {
            await auth.removeUser();
          } catch (e) {
            // Failed to remove user - continue with redirect
          }
          window.location.href = "/";
        }, 1000);
      }
    })();
  }, [auth]);

  if (error) {
    return (
      <LogoutWrapper>
        <p>{t("logout.logout", "Logging out...")}</p>
        <p>{t("logout.redirecting", "Redirecting to homepage...")}</p>
      </LogoutWrapper>
    );
  }

  return (
    <LogoutWrapper>
      <Loading />
      <p>{t("logout.signingOut", "Signing out...")}</p>
    </LogoutWrapper>
  );
};

export default Logout;
